import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom"; // Assuming you use react-router
import {
  Box,
  Card,
  Typography,
  Button,
  Avatar,
  Container,
  Divider,
  Grid,
  Chip,
  useTheme,
  alpha,
  Fade,
  TextField,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Business as BusinessIcon,
  CloudUpload as CloudUploadIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Description as FileIcon,
} from "@mui/icons-material"; // Assuming MUI based on your icon usage
import API from "services/api";
import LoadingScreen from "components/Loading";
import MDButton from "components/MDButton";
import PersonalInfo from "./components/PersonalInfo";
import QuestionsStep from "./components/QuestionsStep";
import SuccessStep from "./components/SuccessStep";
import { useTranslation } from "react-i18next";
import isEmail from "validator/lib/isEmail";

const MAX_FILE_SIZE_MB = 5;

const ExternalForm = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  // 1. Capture the variables from the link
  const { tenantKey, formId, employeeId } = useParams();
  const config = {
    headers: {
      "X-Tenant-ID": tenantKey, // Use the variable from useParams
      "Content-Type": "application/json",
    },
  };

  const [activeStep, setActiveStep] = useState(0);
  const [personalInfo, setPersonalInfo] = useState({
    applicantId: null,
    fullName: "",
    email: "",
    phone: "",
    cv: null, // Stores the file object
    cvName: "", // Stores the file name for display
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setFrom] = useState(null);
  const [formQuestions, setFormQuestions] = useState();
  const [companyData, setCompanyData] = useState(null);
  const [answers, setAnswers] = useState([]);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
  // 2. Fetch the form data on load
  useEffect(() => {
    const fetchFormDetails = async () => {
      // 1. Validation
      if (!tenantKey || !formId) return;

      try {
        setLoading(true);

        // 2. Create the config with the specific Tenant Header

        const response = await API.get(`/job-forms/public/${formId}/${employeeId}`, config);
        const data = response.data;
        console.log("Data Is::", data);
        if (data.form.status === "COMPLETED" || data.form.status === "REJECTED") {
          setError("Form not found, or the link has expired.");
          setLoading(false);
          return;
        }
        setFrom(data.form);
        setCompanyData(data.company);
        setFormQuestions(data.questions);
        // 4. Set the data

        setLoading(false);
      } catch (err) {
        console.error("Error loading form:", err);
        setError("Form not found, or the link has expired.");
        setLoading(false);
      }
    };

    fetchFormDetails();
  }, [tenantKey, formId, employeeId]);

  // --- HANDLERS ---
  const handleChange = (prop) => (event) => {
    setPersonalInfo({ ...personalInfo, [prop]: event.target.value });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // ✅ Check file type (PDF only)
    if (file.type !== "application/pdf") {
      setSnackbar({
        open: true,
        message: "Only PDF files are allowed.",
        severity: "error",
      });
      event.target.value = null; // Clear input
      return;
    }

    const fileSizeMB = file.size / 1024 / 1024; // Convert bytes → MB

    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      // Show an error message or snackbar
      setSnackbar({
        open: true,
        message: `File is too large. Maximum allowed size is ${MAX_FILE_SIZE_MB} MB.`,
        severity: "error",
      });
      event.target.value = null; // Clear the input
      return;
    }

    // File is within size limit
    setPersonalInfo({
      ...personalInfo,
      cv: file,
      cvName: file.name,
    });
  };

  const handleNext = () => {
    const isMissingFields =
      !personalInfo.fullName || !personalInfo.email || !personalInfo.phone || !personalInfo.cv;
    const isInvalidEmail = personalInfo.email && !isEmail(personalInfo.email);

    if (isMissingFields) {
      setSnackbar({
        open: true,
        message: "Please fill in all personal details and upload your CV to proceed.",
        severity: "error",
      });
      return;
    }

    if (isInvalidEmail) {
      setSnackbar({ open: true, message: "Invalid Email!", severity: "error" });
      return;
    }

    setActiveStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setPersonalInfo({
      applicantId: null,
      fullName: "",
      email: "",
      phone: "",
      cv: null, // Stores the file object
      cvName: "", // Stores the file name for display
    });

    setError(null);
    setAnswers([]);
    setActiveStep(0);
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleCheckboxChange = (questionId, option) => {
    setAnswers((prev) => {
      const currentSelections = prev[questionId] || [];
      if (currentSelections.includes(option)) {
        // Remove option if already selected
        return { ...prev, [questionId]: currentSelections.filter((item) => item !== option) };
      } else {
        // Add option
        return { ...prev, [questionId]: [...currentSelections, option] };
      }
    });
  };

  const handleVerification = async (credentials) => {
    try {
      if (!personalInfo.cv) {
        setSnackbar({
          open: true,
          message: `Please upload your CV to proceed.`,
          severity: "error",
        });
        return;
      }
      const response = await API.post(`/employee/verify`, credentials, config);
      const data = response.data;
      if (data && data.verified) {
        setPersonalInfo({
          ...personalInfo,
          fullName: data.name,
          email: data.email,
          phone: data.phoneNumber,
          applicantId: data.id,
        });

        setActiveStep(1);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (data && !data.verified) {
        setSnackbar({
          open: true,
          message: `Employee not found. Please check your ID and Email.`,
          severity: "error",
        });
      }
      return data;
    } catch (e) {}
  };

  const handleFinalSubmit = (e) => {
    e.preventDefault();
    // 1. Validation: Check required fields
    const missingFields = formQuestions.filter(
      (q) =>
        q.isRequired &&
        (!answers[q.id] || (Array.isArray(answers[q.id]) && answers[q.id].length === 0))
    );

    if (missingFields.length > 0) {
      setSnackbar({
        open: true,
        message: `Please answer the following required questions: ${missingFields
          .map((q) => q.orderIndex + 1)
          .join(", ")}`,
        severity: "error",
      });
      return;
    }

    const payload = {
      applicantId: personalInfo?.applicantId,
      fullName: personalInfo?.fullName,
      email: personalInfo?.email,
      phone: personalInfo?.phone,
      cvName: personalInfo?.cvName,
      formId: form?.id,
      answers: Object.entries(answers).map(([qId, val]) => ({
        questionId: qId,
        answer: Array.isArray(val) ? val.join(";") : val,
      })),
      recruiterId: employeeId,
    };
    // 2. FormData to send payload + file
    const formData = new FormData();
    formData.append("data", new Blob([JSON.stringify(payload)], { type: "application/json" }));
    formData.append("cv", personalInfo?.cv);

    try {
      API.post(`/job-applications/save`, formData, {
        headers: {
          "X-Tenant-ID": tenantKey,
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: `Something went wrong while Submitting your data.`,
        severity: "error",
      });
    }
    // Submit to API here...
    setActiveStep(2);
  };

  const logoSrc = useMemo(() => {
    if (!companyData?.companyLogo) return null;

    const logo = companyData.companyLogo;
    if (logo.startsWith("data:image")) return logo;
    return `data:image/png;base64,${logo}`;
  }, [companyData?.companyLogo]);

  const primaryColor = useMemo(
    () => companyData?.primaryColor || theme.palette.primary.main,
    [companyData?.primaryColor]
  );

  const bgColor = useMemo(
    () => companyData?.backgroundColor || "#f4f6f8",
    [companyData?.backgroundColor]
  );

  const textColor = useMemo(() => companyData?.textColor || "#f4f6f8", [companyData?.textColor]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography variant="h5" color="error">
          {error}
        </Typography>
      </Box>
    );
  }
  // 3. Render the Form
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: bgColor,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pb: 2,
      }}
    >
      {/* 1. BRANDED BANNER */}
      {/* This creates the colored strip at the top using the company's color */}
      <Box
        sx={{
          width: "100%",
          height: "240px",
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${alpha(
            primaryColor,
            0.8
          )} 100%)`,
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 0,
          boxShadow: "inset 0 -10px 20px rgba(0,0,0,0.05)",
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, mt: 8 }}>
        {/* 2. MAIN CARD */}
        <Card
          elevation={6}
          sx={{
            overflow: "visible", // Allows the logo to float slightly above if we wanted
            borderRadius: 3,
            padding: { xs: 3, md: 5 },
            backgroundColor: "#ffffff",
          }}
        >
          {/* Company Branding Section */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
            {/* Logo */}
            <Avatar
              src={logoSrc}
              alt={companyData?.companyName}
              sx={{
                width: 100,
                height: 100,
                mb: 2,
                bgcolor: "white",
                border: "4px solid white",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                objectFit: "contain",
                // If logo fails, fallback to icon
                "& img": { objectFit: "contain" },
              }}
            >
              <BusinessIcon sx={{ fontSize: 50, color: primaryColor }} />
            </Avatar>

            {/* Company Name */}
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              {companyData?.companyName || "Company Name"}
            </Typography>

            {/* Optional: Mission/Vision snippet if it exists */}
            {companyData?.vision && (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mt: 1, maxWidth: "80%", fontStyle: "italic" }}
              >
                {`"${companyData.vision.substring(0, 150)}..."`}
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Form Title Section */}
          <Box mb={4}>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Typography variant="h3" fontWeight="bold" sx={{ color: "#344767" }}>
                {/* {form?.title || "Application Form"} */}
                {t("admin.applicants.app_form")}
              </Typography>
            </Box>

            <Chip
              label={`Job Questions #${formQuestions?.length + 3}`}
              size="small"
              sx={{ bgcolor: alpha(primaryColor, 0.1), color: primaryColor, fontWeight: "bold" }}
            />

            {activeStep !== 2 && (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Please complete all required fields below and submit your application.
              </Typography>
            )}
          </Box>

          {/* 3. DYNAMIC FORM CONTENT */}
          <Box>
            {/* --- STEP 1: PERSONAL INFO --- */}
            {activeStep === 0 ? (
              <PersonalInfo
                handleChange={handleChange}
                handleFileChange={handleFileChange}
                handleNext={handleNext}
                primaryColor={primaryColor}
                bgColor={bgColor}
                activeStep={activeStep}
                personalInfo={personalInfo}
                setPersonalInfo={setPersonalInfo}
                handleVerification={handleVerification}
              />
            ) : activeStep === 1 ? (
              <Fade in={activeStep === 1}>
                <Box>
                  <QuestionsStep
                    questions={formQuestions}
                    answers={answers}
                    handleAnswerChange={handleAnswerChange}
                    handleCheckboxChange={handleCheckboxChange}
                    handleBack={handleBack}
                    handleSubmit={handleFinalSubmit}
                    primaryColor={primaryColor}
                    bgColor={bgColor}
                  />
                </Box>
              </Fade>
            ) : (
              // --- STEP 2: SUCCESS MESSAGE ---
              <Fade in={activeStep === 2}>
                <Box>
                  <SuccessStep primaryColor={primaryColor} companyName={companyData?.companyName} />
                </Box>
              </Fade>
            )}
          </Box>
        </Card>

        {/* Footer */}
        <Box mt={4} textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Powered by HRHealthOne &copy; {new Date().getFullYear()}
          </Typography>
        </Box>
      </Container>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExternalForm;
